'use client';


import { round } from 'es-toolkit';
import { t } from 'mobx-state-tree';
import * as THREE from 'three';

import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import type { Instance, SnapshotIn } from 'mobx-state-tree';

import { UndoRedoActionType } from '@/editor/core/undoRedo/undoRedoTypes';



export const Vector2 = t.custom<THREE.Vector2Like, THREE.Vector2>({
  name: 'Vector2',
  fromSnapshot(value) { return new THREE.Vector2(value.x, value.y); },
  toSnapshot(value) { return { x: value.x, y: value.y }; },
  isTargetType(value) { return value instanceof THREE.Vector2; },
  getValidationMessage(value) { return 'Value must be Vector2Like'; }
});

export const Vector3 = t.custom<THREE.Vector3Like, THREE.Vector3>({
  name: 'Vector3',
  fromSnapshot(value) { return new THREE.Vector3(value.x, value.y, value.z); },
  toSnapshot(value) { return { x: value.x, y: value.y, z: value.z }; },
  isTargetType(value) { return value instanceof THREE.Vector3; },
  getValidationMessage(value) { return 'Value must be Vector3Like'; }
});

export const Vector4 = t.custom<THREE.Vector4Like, THREE.Vector4>({
  name: 'Vector4',
  fromSnapshot(value) { return new THREE.Vector4(value.x, value.y, value.z, value.w); },
  toSnapshot(value) { return { x: value.x, y: value.y, z: value.z, w: value.w }; },
  isTargetType(value) { return value instanceof THREE.Vector4; },
  getValidationMessage(value) { return 'Value must be Vector4Like'; }
});


/**
 * Model for 2D vector property
 * NOTE: Use Vector2Property.create() to create a vector2 property
 */
export const Vector2PropertyBase = t.compose('Vector2Property', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Vector2),
    value: t.optional(Vector2, () => new THREE.Vector2(0, 0)),
    defaultValue: t.optional(Vector2, () => new THREE.Vector2(0, 0)),
    min: t.maybe(t.number),
    max: t.maybe(t.number),
    step: t.optional(t.number, 0.01),
    decimalPlaces: t.maybe(t.number), // Clamps the value to specified decimal places i.e. use 0 for integer values
    showLinking: t.optional(t.boolean, false), // Whether to show the linking feature (button)
    linked: t.optional(t.boolean, false) // Whether the vector components are linked together
  })
).volatile(() => ({
  preDragValue: null as THREE.Vector2 | null,
})).actions(self => ({
  setDefaultValue(value: THREE.Vector2Like) {
    let finalX = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.x) : value.x) : value.x;
    let finalY = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.y) : value.y) : value.y;

    if (self.decimalPlaces !== undefined) {
      finalX = round(finalX, self.decimalPlaces);
      finalY = round(finalY, self.decimalPlaces);
    }

    self.defaultValue = new THREE.Vector2(finalX, finalY);
  },
  setMin(min: number) { self.min = min; },
  setMax(max: number) { self.max = max; },
  setStep(step: number) { self.step = step; },
  setDecimalPlaces(dp: number) { self.decimalPlaces = dp; },
  toggleLinked() {
    self.linked = !self.linked;
  },
  setAxisValue(axis: 'x' | 'y', value: number, actionType?: UndoRedoActionType, fromPanel = true, initial = false) {
    const resolvedActionType = actionType ?? UndoRedoActionType.Primary;
    const currentX = self.value.x;
    const currentY = self.value.y;

    let finalX = currentX;
    let finalY = currentY;

    // Set the specific axis value
    if (axis === 'x') {
      finalX = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
    } else {
      finalY = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
    }

    // Apply linked scaling if enabled and not initial value
    if (self.linked && !initial && fromPanel) {
      if (axis === 'x' && currentX !== 0) {
        const ratio = finalX / currentX;
        finalY = currentY * ratio;
      } else if (axis === 'y' && currentY !== 0) {
        const ratio = finalY / currentY;
        finalX = currentX * ratio;
      }

      // Re-apply constraints after scaling
      finalX = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalX) : finalX) : finalX;
      finalY = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalY) : finalY) : finalY;
    }

    if (self.decimalPlaces !== undefined) {
      finalX = round(finalX, self.decimalPlaces);
      finalY = round(finalY, self.decimalPlaces);
    }

    if (finalX === self.value.x && finalY === self.value.y) return;

    if (resolvedActionType === UndoRedoActionType.Silent) {
      if (self.preDragValue === null) {
        self.preDragValue = self.value.clone();
      }
      self.notifyValueChange(self.path, { x: finalX, y: finalY }, { fromPanel, initial, property: self as Instance<typeof Vector2Property>, actionType: resolvedActionType });
      return;
    }

    const preDragValue = self.preDragValue;
    self.preDragValue = null;
    self.value = new THREE.Vector2(finalX, finalY);
    self.notifyValueChange(self.path, { x: finalX, y: finalY }, { fromPanel, initial, property: self as Instance<typeof Vector2Property>, actionType: resolvedActionType, preDragValue: preDragValue ?? undefined });
  }
})).actions(self => ({
  setValue(value: THREE.Vector2Like, fromPanel = true, initial = false, actionType: UndoRedoActionType = UndoRedoActionType.Primary) {
    let finalX = initial ? value.x : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.x) : value.x) : value.x);
    let finalY = initial ? value.y : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.y) : value.y) : value.y);

    if (self.decimalPlaces !== undefined) {
      finalX = round(finalX, self.decimalPlaces);
      finalY = round(finalY, self.decimalPlaces);
    }

    if (finalX === self.value.x && finalY === self.value.y) return;

    if (actionType === UndoRedoActionType.Silent) {
      if (self.preDragValue === null) {
        self.preDragValue = self.value.clone();
      }
      self.notifyValueChange(self.path, { x: finalX, y: finalY }, { fromPanel, initial, property: self as Instance<typeof Vector2Property>, actionType });
      return;
    }

    const preDragValue = self.preDragValue;
    self.preDragValue = null;
    self.value = new THREE.Vector2(finalX, finalY);
    self.notifyValueChange(self.path, { x: finalX, y: finalY }, { fromPanel, initial, property: self as Instance<typeof Vector2Property>, actionType, preDragValue: preDragValue ?? undefined });
  }
})).views(self => ({
  get shouldShowResetButton(): boolean {
    // Show reset button when value differs from default (works for both prefabs and metadata defaults)
    // Don't show reset button for readonly properties
    if (self.readOnly) return false;
    return !self.value.equals(self.defaultValue);
  }
})).actions(self => ({
  resetToDefault() {
    // Clone the default value to avoid reference issues
    const defaultValue = self.defaultValue.clone();
    self.setValue(defaultValue, true, false);
  }
}));

export const Vector2Property = t.snapshotProcessor(Vector2PropertyBase, {
  preProcessor(snapshot: SnapshotIn<typeof Vector2PropertyBase>) {
    return {
      ...snapshot,
      value: snapshot.value instanceof THREE.Vector2 ? snapshot.value.clone() : new THREE.Vector2(0, 0),
      defaultValue: snapshot.defaultValue instanceof THREE.Vector2 ? snapshot.defaultValue.clone() : new THREE.Vector2(0, 0),
      // If showLinking is enabled and linked is not explicitly set, default to true
      linked: snapshot.showLinking && snapshot.linked === undefined ? true : (snapshot.linked ?? false)
    };
  }
});


/**
 * Model for 3D vector property
 * NOTE: Use Vector3Property.create() to create a vector3 property
 */
export const Vector3PropertyBase = t.compose('Vector3Property', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Vector3),
    value: t.optional(Vector3, () => new THREE.Vector3(0, 0, 0)),
    defaultValue: t.optional(Vector3, () => new THREE.Vector3(0, 0, 0)),
    min: t.maybe(t.number),
    max: t.maybe(t.number),
    step: t.optional(t.number, 0.01),
    decimalPlaces: t.maybe(t.number), // Clamps the value to specified decimal places i.e. use 0 for integer values
    showLinking: t.optional(t.boolean, false), // Whether to show the linking feature (button)
    linked: t.optional(t.boolean, false), // Whether the vector components are linked together
    showEulerAsDegrees: t.optional(t.boolean, true), // Whether to show the euler angles as degrees
  })).volatile(() => ({
  preDragValue: null as THREE.Vector3 | null,
}))
  .actions(self => ({
    setDefaultValue(value: THREE.Vector3Like) {
      let finalX = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.x) : value.x) : value.x;
      let finalY = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.y) : value.y) : value.y;
      let finalZ = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.z) : value.z) : value.z;

      if (self.decimalPlaces !== undefined) {
        finalX = round(finalX, self.decimalPlaces);
        finalY = round(finalY, self.decimalPlaces);
        finalZ = round(finalZ, self.decimalPlaces);
      }

      self.defaultValue = new THREE.Vector3(finalX, finalY, finalZ);
    },
    setMin(min: number) { self.min = min; },
    setMax(max: number) { self.max = max; },
    setStep(step: number) { self.step = step; },
    setDecimalPlaces(dp: number) { self.decimalPlaces = dp; },
    toggleLinked() {
      self.linked = !self.linked;
    },
    setAxisValue(axis: 'x' | 'y' | 'z', value: number, actionType?: UndoRedoActionType, fromPanel = true, initial = false) {
      const resolvedActionType = actionType ?? UndoRedoActionType.Primary;
      const currentX = self.value.x;
      const currentY = self.value.y;
      const currentZ = self.value.z;

      let finalX = currentX;
      let finalY = currentY;
      let finalZ = currentZ;

      // Set the specific axis value
      if (axis === 'x') {
        finalX = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
      } else if (axis === 'y') {
        finalY = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
      } else {
        finalZ = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
      }

      // Apply linked scaling if enabled and not initial value
      if (self.linked && !initial && fromPanel) {
        if (axis === 'x' && currentX !== 0) {
          const ratio = finalX / currentX;
          finalY = currentY * ratio;
          finalZ = currentZ * ratio;
        } else if (axis === 'y' && currentY !== 0) {
          const ratio = finalY / currentY;
          finalX = currentX * ratio;
          finalZ = currentZ * ratio;
        } else if (axis === 'z' && currentZ !== 0) {
          const ratio = finalZ / currentZ;
          finalX = currentX * ratio;
          finalY = currentY * ratio;
        }

        // Re-apply constraints after scaling
        finalX = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalX) : finalX) : finalX;
        finalY = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalY) : finalY) : finalY;
        finalZ = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalZ) : finalZ) : finalZ;
      }

      if (self.decimalPlaces !== undefined) {
        finalX = round(finalX, self.decimalPlaces);
        finalY = round(finalY, self.decimalPlaces);
        finalZ = round(finalZ, self.decimalPlaces);
      }

      if (finalX === self.value.x && finalY === self.value.y && finalZ === self.value.z) return;

      if (resolvedActionType === UndoRedoActionType.Silent) {
        if (self.preDragValue === null) {
          self.preDragValue = self.value.clone();
        }
        self.notifyValueChange(self.path, { x: finalX, y: finalY, z: finalZ }, { fromPanel, initial, property: self as Instance<typeof Vector3Property>, actionType: resolvedActionType });
        return;
      }

      const preDragValue = self.preDragValue;
      self.preDragValue = null;
      self.value = new THREE.Vector3(finalX, finalY, finalZ);
      self.notifyValueChange(self.path, { x: finalX, y: finalY, z: finalZ }, { fromPanel, initial, property: self as Instance<typeof Vector3Property>, actionType: resolvedActionType, preDragValue: preDragValue ?? undefined });
    }
  }))
  .actions(self => ({
    setValue(value: THREE.Vector3Like, fromPanel = true, initial = false, actionType: UndoRedoActionType = UndoRedoActionType.Primary) {
      if (self.showEulerAsDegrees && value instanceof THREE.Euler) {
        value = new THREE.Vector3(THREE.MathUtils.radToDeg(value.x), THREE.MathUtils.radToDeg(value.y), THREE.MathUtils.radToDeg(value.z));
      }
      let finalX = initial ? value.x : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.x) : value.x) : value.x);
      let finalY = initial ? value.y : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.y) : value.y) : value.y);
      let finalZ = initial ? value.z : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.z) : value.z) : value.z);

      if (self.decimalPlaces !== undefined) {
        finalX = round(finalX, self.decimalPlaces);
        finalY = round(finalY, self.decimalPlaces);
        finalZ = round(finalZ, self.decimalPlaces);
      }

      if (finalX === self.value.x && finalY === self.value.y && finalZ === self.value.z) return;

      if (actionType === UndoRedoActionType.Silent) {
        if (self.preDragValue === null) {
          self.preDragValue = self.value.clone();
        }
        self.notifyValueChange(self.path, { x: finalX, y: finalY, z: finalZ }, { fromPanel, initial, property: self as Instance<typeof Vector3Property>, actionType });
        return;
      }

      const preDragValue = self.preDragValue;
      self.preDragValue = null;
      self.value = new THREE.Vector3(finalX, finalY, finalZ);
      self.notifyValueChange(self.path, { x: finalX, y: finalY, z: finalZ }, { fromPanel, initial, property: self as Instance<typeof Vector3Property>, actionType, preDragValue: preDragValue ?? undefined });
    }
  }))
  .views(self => ({
    get shouldShowResetButton(): boolean {
      // Show reset button when value differs from default (works for both prefabs and metadata defaults)
      // Don't show reset button for readonly properties
      if (self.readOnly) return false;
      return !self.value.equals(self.defaultValue);
    }
  }))
  .actions(self => ({
    resetToDefault() {
      // Clone the default value to avoid reference issues
      const defaultValue = self.defaultValue.clone();
      self.setValue(defaultValue, true, false);
    }
  }));

export const Vector3Property = t.snapshotProcessor(Vector3PropertyBase, {
  preProcessor(snapshot: SnapshotIn<typeof Vector3PropertyBase>) {
    return {
      ...snapshot,
      value: snapshot.value instanceof THREE.Vector3 ? snapshot.value.clone() : new THREE.Vector3(0, 0, 0),
      defaultValue: snapshot.defaultValue instanceof THREE.Vector3 ? snapshot.defaultValue.clone() : new THREE.Vector3(0, 0, 0),
      // If showLinking is enabled and linked is not explicitly set, default to true
      linked: snapshot.showLinking && snapshot.linked === undefined ? true : (snapshot.linked ?? false)
    };
  }
});


/**
 * Model for 4D vector property
 * NOTE: Use Vector4Property.create() to create a vector4 property
 */
export const Vector4PropertyBase = t.compose('Vector4Property', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Vector4),
    value: t.optional(Vector4, () => new THREE.Vector4(0, 0, 0, 0)),
    defaultValue: t.optional(Vector4, () => new THREE.Vector4(0, 0, 0, 0)),
    min: t.maybe(t.number),
    max: t.maybe(t.number),
    step: t.optional(t.number, 0.01),
    decimalPlaces: t.maybe(t.number), // Clamps the value to specified decimal places i.e. use 0 for integer values
    showLinking: t.optional(t.boolean, false), // Whether to show the linking feature (button)
    linked: t.optional(t.boolean, false) // Whether the vector components are linked together
  })
).volatile(() => ({
  preDragValue: null as THREE.Vector4 | null,
})).actions(self => ({
  setDefaultValue(value: THREE.Vector4Like) {
    let finalX = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.x) : value.x) : value.x;
    let finalY = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.y) : value.y) : value.y;
    let finalZ = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.z) : value.z) : value.z;
    let finalW = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.w) : value.w) : value.w;

    if (self.decimalPlaces !== undefined) {
      finalX = round(finalX, self.decimalPlaces);
      finalY = round(finalY, self.decimalPlaces);
      finalZ = round(finalZ, self.decimalPlaces);
      finalW = round(finalW, self.decimalPlaces);
    }

    self.defaultValue = new THREE.Vector4(finalX, finalY, finalZ, finalW);
  },
  setMin(min: number) { self.min = min; },
  setMax(max: number) { self.max = max; },
  setStep(step: number) { self.step = step; },
  setDecimalPlaces(dp: number) { self.decimalPlaces = dp; },
  toggleLinked() {
    self.linked = !self.linked;
  },
  setAxisValue(axis: 'x' | 'y' | 'z' | 'w', value: number, actionType?: UndoRedoActionType, fromPanel = true, initial = false) {
    const resolvedActionType = actionType ?? UndoRedoActionType.Primary;
    const currentX = self.value.x;
    const currentY = self.value.y;
    const currentZ = self.value.z;
    const currentW = self.value.w;

    let finalX = currentX;
    let finalY = currentY;
    let finalZ = currentZ;
    let finalW = currentW;

    // Set the specific axis value
    if (axis === 'x') {
      finalX = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
    } else if (axis === 'y') {
      finalY = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
    } else if (axis === 'z') {
      finalZ = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
    } else {
      finalW = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
    }

    // Apply linked scaling if enabled and not initial value
    if (self.linked && !initial && fromPanel) {
      if (axis === 'x' && currentX !== 0) {
        const ratio = finalX / currentX;
        finalY = currentY * ratio;
        finalZ = currentZ * ratio;
        finalW = currentW * ratio;
      } else if (axis === 'y' && currentY !== 0) {
        const ratio = finalY / currentY;
        finalX = currentX * ratio;
        finalZ = currentZ * ratio;
        finalW = currentW * ratio;
      } else if (axis === 'z' && currentZ !== 0) {
        const ratio = finalZ / currentZ;
        finalX = currentX * ratio;
        finalY = currentY * ratio;
        finalW = currentW * ratio;
      } else if (axis === 'w' && currentW !== 0) {
        const ratio = finalW / currentW;
        finalX = currentX * ratio;
        finalY = currentY * ratio;
        finalZ = currentZ * ratio;
      }

      // Re-apply constraints after scaling
      finalX = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalX) : finalX) : finalX;
      finalY = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalY) : finalY) : finalY;
      finalZ = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalZ) : finalZ) : finalZ;
      finalW = self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, finalW) : finalW) : finalW;
    }

    if (self.decimalPlaces !== undefined) {
      finalX = round(finalX, self.decimalPlaces);
      finalY = round(finalY, self.decimalPlaces);
      finalZ = round(finalZ, self.decimalPlaces);
      finalW = round(finalW, self.decimalPlaces);
    }

    if (finalX === self.value.x && finalY === self.value.y && finalZ === self.value.z && finalW === self.value.w) return;

    if (resolvedActionType === UndoRedoActionType.Silent) {
      if (self.preDragValue === null) {
        self.preDragValue = self.value.clone();
      }
      self.notifyValueChange(self.path, { x: finalX, y: finalY, z: finalZ, w: finalW }, { fromPanel, initial, property: self as Instance<typeof Vector4Property>, actionType: resolvedActionType });
      return;
    }

    const preDragValue = self.preDragValue;
    self.preDragValue = null;
    self.value = new THREE.Vector4(finalX, finalY, finalZ, finalW);
    self.notifyValueChange(self.path, { x: finalX, y: finalY, z: finalZ, w: finalW }, { fromPanel, initial, property: self as Instance<typeof Vector4Property>, actionType: resolvedActionType, preDragValue: preDragValue ?? undefined });
  }
})).actions(self => ({
  setValue(value: THREE.Vector4Like, fromPanel = true, initial = false, actionType: UndoRedoActionType = UndoRedoActionType.Primary) {
    let finalX = initial ? value.x : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.x) : value.x) : value.x);
    let finalY = initial ? value.y : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.y) : value.y) : value.y);
    let finalZ = initial ? value.z : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.z) : value.z) : value.z);
    let finalW = initial ? value.w : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value.w) : value.w) : value.w);

    if (self.decimalPlaces !== undefined) {
      finalX = round(finalX, self.decimalPlaces);
      finalY = round(finalY, self.decimalPlaces);
      finalZ = round(finalZ, self.decimalPlaces);
      finalW = round(finalW, self.decimalPlaces);
    }

    if (finalX === self.value.x && finalY === self.value.y && finalZ === self.value.z && finalW === self.value.w) return;

    if (actionType === UndoRedoActionType.Silent) {
      if (self.preDragValue === null) {
        self.preDragValue = self.value.clone();
      }
      self.notifyValueChange(self.path, { x: finalX, y: finalY, z: finalZ, w: finalW }, { fromPanel, initial, property: self as Instance<typeof Vector4Property>, actionType });
      return;
    }

    const preDragValue = self.preDragValue;
    self.preDragValue = null;
    self.value = new THREE.Vector4(finalX, finalY, finalZ, finalW);
    self.notifyValueChange(self.path, { x: finalX, y: finalY, z: finalZ, w: finalW }, { fromPanel, initial, property: self as Instance<typeof Vector4Property>, actionType, preDragValue: preDragValue ?? undefined });
  }
})).views(self => ({
}));

export const Vector4Property = t.snapshotProcessor(Vector4PropertyBase, {
  preProcessor(snapshot: SnapshotIn<typeof Vector4PropertyBase>) {
    return {
      ...snapshot,
      value: snapshot.value instanceof THREE.Vector4 ? snapshot.value.clone() : new THREE.Vector4(0, 0, 0, 0),
      defaultValue: snapshot.defaultValue instanceof THREE.Vector4 ? snapshot.defaultValue.clone() : new THREE.Vector4(0, 0, 0, 0),
      // If showLinking is enabled and linked is not explicitly set, default to true
      linked: snapshot.showLinking && snapshot.linked === undefined ? true : (snapshot.linked ?? false)
    };
  }
});


// ---------------------------------------------------------------------------
// Euler property
// ---------------------------------------------------------------------------
// The engine stores THREE.Euler values in RADIANS.
// The editor displays them in DEGREES for user convenience.
//
// Convention in this model:
//   - `value` / `defaultValue` are stored in DEGREES (display units)
//   - `setValue(euler)` receives radians from the engine and converts to degrees
//   - `notifyValueChange` emits a THREE.Euler in RADIANS so the engine receives
//     the correct unit when the user edits a value from the panel.

type EulerLike = { x: number; y: number; z: number; order?: string };

export const Euler = t.custom<EulerLike, THREE.Euler>({
  name: 'Euler',
  fromSnapshot(value) { return new THREE.Euler(value.x, value.y, value.z); },
  toSnapshot(value) { return { x: value.x, y: value.y, z: value.z }; },
  isTargetType(value) { return value instanceof THREE.Euler; },
  getValidationMessage(_value) { return 'Value must be EulerLike'; }
});

/**
 * Model for Euler rotation property.
 * Internally stores values in DEGREES for display.
 * Emits values in RADIANS to the engine via notifyValueChange.
 * NOTE: Use EulerProperty.create() to create an euler property.
 */
export const EulerPropertyBase = t.compose('EulerProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Euler),
    /** Internal storage in DEGREES */
    value: t.optional(Vector3, () => new THREE.Vector3(0, 0, 0)),
    /** Default value in DEGREES */
    defaultValue: t.optional(Vector3, () => new THREE.Vector3(0, 0, 0)),
    min: t.maybe(t.number),
    max: t.maybe(t.number),
    step: t.optional(t.number, 0.01),
    // Default to 4 decimal places to absorb floating-point noise from rad↔deg
    // conversion (e.g. radToDeg(degToRad(12)) = 12.000000000000002).
    // Users can override via metadata: @property({ type: 'euler', decimals: 2 }).
    decimalPlaces: t.optional(t.number, 4),
    /** Euler rotation order (e.g. 'XYZ') */
    eulerOrder: t.optional(t.string, 'XYZ'),
  })
).actions(self => ({
  setMin(min: number) { self.min = min; },
  setMax(max: number) { self.max = max; },
  setStep(step: number) { self.step = step; },
  setDecimalPlaces(dp: number) { self.decimalPlaces = dp; },
  setEulerOrder(order: string) { self.eulerOrder = order; },

  setDefaultValue(value: THREE.Euler | THREE.Vector3Like) {
    // Accept either a THREE.Euler (radians) or a Vector3Like (treated as degrees)
    let dx: number, dy: number, dz: number;
    if (value instanceof THREE.Euler) {
      dx = THREE.MathUtils.radToDeg(value.x);
      dy = THREE.MathUtils.radToDeg(value.y);
      dz = THREE.MathUtils.radToDeg(value.z);
    } else {
      dx = (value as THREE.Vector3Like).x;
      dy = (value as THREE.Vector3Like).y;
      dz = (value as THREE.Vector3Like).z;
    }
    if (self.decimalPlaces !== undefined) {
      dx = round(dx, self.decimalPlaces);
      dy = round(dy, self.decimalPlaces);
      dz = round(dz, self.decimalPlaces);
    }
    self.defaultValue = new THREE.Vector3(dx, dy, dz);
  },

  /**
   * Set an individual axis value (in DEGREES, from the panel).
   * Emits a THREE.Euler in RADIANS to the engine.
   */
  setAxisValue(axis: 'x' | 'y' | 'z', valueDeg: number, actionType?: UndoRedoActionType, fromPanel = true, initial = false) {
    let finalX = self.value.x;
    let finalY = self.value.y;
    let finalZ = self.value.z;

    const clamp = (v: number) =>
      self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, v) : v) : v;

    if (axis === 'x') finalX = initial ? valueDeg : clamp(valueDeg);
    else if (axis === 'y') finalY = initial ? valueDeg : clamp(valueDeg);
    else finalZ = initial ? valueDeg : clamp(valueDeg);

    if (self.decimalPlaces !== undefined) {
      finalX = round(finalX, self.decimalPlaces);
      finalY = round(finalY, self.decimalPlaces);
      finalZ = round(finalZ, self.decimalPlaces);
    }

    if (finalX === self.value.x && finalY === self.value.y && finalZ === self.value.z) return;

    self.value = new THREE.Vector3(finalX, finalY, finalZ);

    // Emit radians to the engine
    const eulerRad = new THREE.Euler(
      THREE.MathUtils.degToRad(finalX),
      THREE.MathUtils.degToRad(finalY),
      THREE.MathUtils.degToRad(finalZ),
      self.eulerOrder as THREE.EulerOrder
    );
    self.notifyValueChange(self.path, eulerRad, { fromPanel, initial, property: self as Instance<typeof EulerProperty>, actionType });
  }
})).actions(self => ({
  /**
   * Called when the engine updates the property.
   * `value` is a THREE.Euler in RADIANS (or a plain Vector3Like in degrees for initial/snapshot).
   */
  setValue(value: THREE.Euler | THREE.Vector3Like, fromPanel = true, initial = false) {
    let dx: number, dy: number, dz: number;

    if (value instanceof THREE.Euler) {
      // Engine sends radians — convert to degrees for display
      dx = THREE.MathUtils.radToDeg(value.x);
      dy = THREE.MathUtils.radToDeg(value.y);
      dz = THREE.MathUtils.radToDeg(value.z);
    } else {
      // Plain object — treat as degrees (e.g. snapshot / default value)
      dx = (value as THREE.Vector3Like).x;
      dy = (value as THREE.Vector3Like).y;
      dz = (value as THREE.Vector3Like).z;
    }

    const clamp = (v: number) =>
      (!initial && self.min !== undefined) ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, v) : v) : v;

    let finalX = clamp(dx);
    let finalY = clamp(dy);
    let finalZ = clamp(dz);

    if (self.decimalPlaces !== undefined) {
      finalX = round(finalX, self.decimalPlaces);
      finalY = round(finalY, self.decimalPlaces);
      finalZ = round(finalZ, self.decimalPlaces);
    }

    if (finalX === self.value.x && finalY === self.value.y && finalZ === self.value.z) return;

    self.value = new THREE.Vector3(finalX, finalY, finalZ);

    // Emit radians to the engine
    const eulerRad = new THREE.Euler(
      THREE.MathUtils.degToRad(finalX),
      THREE.MathUtils.degToRad(finalY),
      THREE.MathUtils.degToRad(finalZ),
      self.eulerOrder as THREE.EulerOrder
    );
    self.notifyValueChange(self.path, eulerRad, { fromPanel, initial, property: self as Instance<typeof EulerProperty> });
  }
})).views(self => ({
  get shouldShowResetButton(): boolean {
    if (self.readOnly) return false;
    return !self.value.equals(self.defaultValue);
  }
})).actions(self => ({
  resetToDefault() {
    const defaultValue = self.defaultValue.clone();
    // defaultValue is in degrees — pass as Vector3Like so setValue treats it as degrees
    self.setValue(defaultValue as unknown as THREE.Vector3Like, true, false);
  }
}));

export const EulerProperty = t.snapshotProcessor(EulerPropertyBase, {
  preProcessor(snapshot: SnapshotIn<typeof EulerPropertyBase>) {
    return {
      ...snapshot,
      value: snapshot.value instanceof THREE.Vector3 ? snapshot.value.clone() : new THREE.Vector3(0, 0, 0),
      defaultValue: snapshot.defaultValue instanceof THREE.Vector3 ? snapshot.defaultValue.clone() : new THREE.Vector3(0, 0, 0),
    };
  }
});
